import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Shield, ScrollText, Info, Scale, Users, HelpCircle, Lock } from 'lucide-react';
import { setPageSeo } from '@/lib/seo';
import axios from 'axios';
import { API_URL } from '@/lib/api';

const UPDATED_AT = 'May 23, 2026';

const pages = {
  about: {
    title: 'About Secrets Club',
    eyebrow: 'Anonymous confessions and advice',
    icon: Info,
    settingsField: 'about_text',
    description: 'Learn what Secrets Club is and how the community works.',
    bodyClassName: 'text-foreground/85',
  },
  rules: {
    title: 'Community Rules',
    eyebrow: 'How to participate safely',
    icon: ScrollText,
    description: 'These rules apply to public submissions, comments, usernames, profile text, images, and any other content members contribute to Secrets Club.',
  },
  'how-it-works': {
    title: 'How It Works',
    eyebrow: 'Anonymous submissions, moderation, and participation',
    icon: Lock,
    settingsField: 'how_it_works_text',
    description: 'How Secrets Club handles anonymous submissions, moderation, accounts, and participation.',
  },
  faq: {
    title: 'FAQ',
    eyebrow: 'Common questions',
    icon: HelpCircle,
    settingsField: 'faq_text',
    description: 'Answers to common questions about Secrets Club.',
  },
  privacy: {
    title: 'Privacy Policy',
    eyebrow: `Last updated ${UPDATED_AT}`,
    icon: Shield,
    settingsField: 'privacy_text',
    description: 'Secrets Club privacy policy.',
  },
  terms: {
    title: 'Terms of Service',
    eyebrow: `Last updated ${UPDATED_AT}`,
    icon: Scale,
    settingsField: 'terms_text',
    description: 'Secrets Club terms of service.',
  },
  contact: {
    title: 'Contact',
    eyebrow: 'Reach the site operator',
    icon: Mail,
    settingsField: 'contact_text',
    description: 'Contact information for Secrets Club.',
  },
  moderation: {
    title: 'Moderation Policy',
    eyebrow: 'How content is reviewed',
    icon: Users,
    settingsField: 'moderation_policy_text',
    description: 'How Secrets Club reviews and moderates content.',
  },
};

const parseTaggedContent = (rawText) => {
  const text = (rawText || '').trim();
  if (!text) return [];

  const blocks = [];
  const headingRegex = /\[heading\]([\s\S]*?)\[\/heading\]/gi;
  let cursor = 0;
  let match;

  const pushParagraphs = (chunk) => {
    chunk
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .forEach((paragraph) => blocks.push({ type: 'paragraph', text: paragraph }));
  };

  while ((match = headingRegex.exec(text)) !== null) {
    pushParagraphs(text.slice(cursor, match.index));
    const heading = match[1].trim();
    if (heading) {
      blocks.push({ type: 'heading', text: heading });
    }
    cursor = headingRegex.lastIndex;
  }

  pushParagraphs(text.slice(cursor));
  return blocks;
};

const TaggedContent = ({ text, bodyClassName = 'text-muted-foreground' }) => {
  const blocks = parseTaggedContent(text);

  if (blocks.length === 0) {
    return (
      <p className="leading-7 text-muted-foreground italic">
        This page is being prepared.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <h2 key={`${block.type}-${index}`} className="font-serif text-2xl font-semibold text-foreground pt-3">
              {block.text}
            </h2>
          );
        }

        return (
          <p key={`${block.type}-${index}`} className={`leading-8 whitespace-pre-wrap ${bodyClassName}`}>
            {block.text}
          </p>
        );
      })}
    </div>
  );
};

const RulesContent = ({ rules }) => {
  if (!rules?.length) {
    return (
      <p className="leading-7 text-muted-foreground italic">
        This page is being prepared.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <p className="text-lg leading-8 text-muted-foreground">
        These rules apply to public submissions, comments, usernames, profile text, images, and any other content members contribute to Secrets Club.
      </p>
      <ol className="space-y-6">
        {rules.map((rule, index) => {
          const title = (rule.title || '').trim();
          const text = (rule.text || '').trim();
          const fallbackTitle = title || text;
          const description = title ? text : '';

          return (
            <li key={rule.rule_id || index} className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {index + 1}. {fallbackTitle}
              </h2>
              {description ? (
                <p className="leading-8 text-muted-foreground pl-5">
                  {description}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const FaqContent = ({ faqItems, fallbackText }) => {
  if (faqItems?.length) {
    return (
      <div className="space-y-7">
        {faqItems.map((item, index) => (
          <section key={item.faq_id || index} className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {item.question}
            </h2>
            <p className="leading-8 text-muted-foreground pl-5 whitespace-pre-wrap">
              {item.answer}
            </p>
          </section>
        ))}
      </div>
    );
  }

  return <TaggedContent text={fallbackText} />;
};

export default function LegalPage({ type }) {
  const content = pages[type] || pages.about;
  const Icon = content.icon;
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/public`)
      .then((res) => setSettings(res.data))
      .catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    const path = type === 'moderation' ? '/moderation-policy' : `/${type}`;
    setPageSeo({
      title: content.title,
      description: content.description,
      path,
    });
  }, [content.description, content.title, type]);

  const settingsText = settings && content.settingsField
    ? settings[content.settingsField] || ''
    : '';
  const rules = settings?.rules || [];
  const faqItems = settings?.faq_items || [];

  const renderBody = () => {
    if (!settings) {
      return (
        <p className="leading-7 text-muted-foreground italic">
          Loading...
        </p>
      );
    }

    if (type === 'rules') {
      return <RulesContent rules={rules} />;
    }

    if (type === 'faq') {
      return <FaqContent faqItems={faqItems} fallbackText={settingsText} />;
    }

    return (
      <TaggedContent
        text={settingsText}
        bodyClassName={content.bodyClassName || 'text-muted-foreground'}
      />
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Card className="bg-card/90 backdrop-blur-sm">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="rounded-2xl bg-primary/10 p-3 text-primary">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{content.eyebrow}</p>
              <h1 className="text-3xl sm:text-4xl font-bold mt-1">{content.title}</h1>
            </div>
          </div>

          {renderBody()}

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/">Back to Confessions</Link>
            </Button>
            {type !== 'contact' ? (
              <Button variant="outline" asChild>
                <Link to="/contact">Contact</Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
